import React, { useEffect } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { getYear, getMonth } from 'date-fns';
import { color, Box, Card, IconButton, Typography, Flex, Button } from '@kistech/kis-ui';
import {
  Grid,
  Typography as MuiTypography,
  Box as MuiBox,
  MenuItem,
  Select,
  Drawer,
  OutlinedInput,
  useTheme
} from '@mui/material';
import { CalendarToday } from '@mui/icons-material';
import { AdminWithActivity } from 'store/admins';
import { Kiosk } from 'store/kiosks';
import { useStyles } from '../styled';

export interface ActivityLogFilterPopUpProps {
  isOpen: boolean;
  onClose: () => void;
  kioskIdsToFilter: string[];
  setKioskIdsToFilter: (kioskIds: string[]) => void;
  usersToFilter: string[];
  setUsersToFilter: (users: string[]) => void;
  selectedStartDate: Date | null;
  setSelectedStartDate: (startDate: Date | null) => void;
  selectedEndDate: Date | null;
  setSelectedEndDate: (endDate: Date | null) => void;
  filterChips: string[];
  setFilterChips: (filterChips: string[]) => void;
  kioskList: Kiosk[];
  admins: AdminWithActivity[];
  handleFilter: () => void;
}

export const ActivityLogFilterPopUp = (props: ActivityLogFilterPopUpProps) => {
  const {
    isOpen,
    onClose,
    kioskIdsToFilter,
    setKioskIdsToFilter,
    usersToFilter,
    setUsersToFilter,
    selectedStartDate,
    setSelectedStartDate,
    selectedEndDate,
    setSelectedEndDate,
    filterChips,
    setFilterChips,
    kioskList,
    admins,
    handleFilter
  } = props;
  const classes = useStyles({ isOpen })();
  const theme = useTheme();

  const currentYear = new Date().getFullYear();
  const years = [currentYear - 1, currentYear];
  const months = [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December'
  ];

  useEffect(() => {
    if (!isOpen) onClose();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelectionChange = (selectionType: string, event: any) => {
    const {
      target: { value }
    } = event;
    const newValue = typeof value === 'string' ? value.split(',') : value;

    if (selectionType === 'kiosk') setKioskIdsToFilter(newValue);
    else if (selectionType === 'user') setUsersToFilter(newValue);
  };

  const onApply = () => {
    const data: string[] = [];
    if (kioskIdsToFilter.length > 0) data.push('Kiosk Name');
    if (usersToFilter.length > 0) data.push('User');
    if (selectedStartDate) data.push('Start Date Time');
    if (selectedEndDate) data.push('End Date Time');

    setFilterChips(data);
    handleFilter();
    onClose();
  };

  const resetFilters = () => {
    setSelectedStartDate(null);
    setSelectedEndDate(null);
    setKioskIdsToFilter([]);
    setUsersToFilter([]);
  };

  const getStyles = (option: string, selectedOptions: string[]) => {
    return {
      fontWeight:
        selectedOptions.indexOf(option) === -1 ? theme.typography.fontWeightRegular : theme.typography.fontWeightMedium,
      fontFamily: 'Poppins, sans-serif'
    };
  };

  const datePickerCustomHeader = ({
    date,
    changeYear,
    changeMonth,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  }: any) => (
    <div
      style={{
        margin: 10,
        display: 'flex',
        justifyContent: 'center'
      }}
    >
      <button
        style={{ marginRight: '15px', color: '#F46D2E' }}
        onClick={decreaseMonth}
        disabled={prevMonthButtonDisabled}
      >
        {'<'}
      </button>
      <select
        style={{ marginRight: '15px' }}
        value={getYear(date)}
        onChange={({ target: { value } }) => changeYear(parseInt(value))}
      >
        {years.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      <select
        style={{ marginRight: '15px' }}
        value={months[getMonth(date)]}
        onChange={({ target: { value } }) => changeMonth(months.indexOf(value))}
      >
        {months.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </select>

      <button style={{ color: '#F46D2E' }} onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
        {'>'}
      </button>
    </div>
  );

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const DatePickerCustomInput = React.forwardRef((props1: any, ref: any) => {
    return (
      <div onClick={props1.onClick} ref={ref} className={classes.datePickerInput}>
        <label>{props1.value || props1.placeholder}</label>
        <div style={{ color: '#F46D2E', marginLeft: '140px' }}>
          <CalendarToday />
        </div>
      </div>
    );
  });

  const disableButton = !selectedStartDate && !selectedEndDate && !kioskIdsToFilter[0] && !usersToFilter[0];

  return (
    <Drawer anchor={'right'} open={isOpen} onClose={onClose}>
      <Grid direction="column" className={classes.drawerContainer}>
        <Grid container direction="row" className={classes.drawerHeaderContainer}>
          <MuiTypography className={classes.drawerHeaderText}>Filters</MuiTypography>
          <MuiBox className={classes.drawerCloseIcon}>
            <IconButton
              name="exit_circle"
              appearance="transparent"
              size={40}
              onClick={() => {
                if (filterChips && filterChips.length === 0) {
                  resetFilters();
                }
                onClose();
              }}
              data-testid="ticket-log-x"
            />
          </MuiBox>
        </Grid>
        <Card.Divider color={color.BORDER} />
        <Grid direction="column">
          <>
            <Box padding="2 7" fullWidth bg="light-gray">
              <Typography weight={600} size={16}>
                Activity Log
              </Typography>
            </Box>
            <Grid direction="row">
              <Grid direction="column" className={classes.filterFormContainer}>
                {/* Start time filter */}
                <Grid item>
                  <MuiBox className={classes.filterCategoryBox}>
                    <MuiTypography className={classes.filterCategoryLabel}>Start Order Time</MuiTypography>
                    <div className="my-datepicker-wrapper">
                      <div className={classes.datePickerContainer}>
                        <DatePicker
                          showTimeSelect
                          renderCustomHeader={datePickerCustomHeader}
                          todayButton={'Today'}
                          placeholderText={'MM/DD/YYYY hh:mm aa'}
                          selected={selectedStartDate}
                          onChange={(date: Date | null) => {
                            setSelectedStartDate(date);
                          }}
                          isClearable
                          dateFormat="MM/dd/yyyy h:mm aa"
                          className={classes.datePickerComponent}
                          customInput={<DatePickerCustomInput />}
                          calendarClassName="my-custom-calendar"
                        />
                      </div>
                    </div>
                  </MuiBox>
                </Grid>
                {/* End time filter */}
                <Grid item>
                  <MuiBox className={classes.filterCategoryBox}>
                    <MuiTypography className={classes.filterCategoryLabel}>End Order Time</MuiTypography>
                    <div className="my-datepicker-wrapper">
                      <div className={classes.datePickerContainer}>
                        <DatePicker
                          showTimeSelect
                          renderCustomHeader={datePickerCustomHeader}
                          todayButton={'Today'}
                          placeholderText={'MM/DD/YYYY hh:mm aa'}
                          selected={selectedEndDate}
                          onChange={(date: Date | null) => {
                            setSelectedEndDate(date);
                          }}
                          isClearable
                          dateFormat="MM/dd/yyyy h:mm aa"
                          className={classes.datePickerComponent}
                          customInput={<DatePickerCustomInput />}
                          calendarClassName="my-custom-calendar"
                        />
                      </div>
                    </div>
                  </MuiBox>
                </Grid>
                {/* Kiosk names filter */}
                <Grid item>
                  <MuiBox className={classes.filterCategoryBox}>
                    <MuiTypography className={classes.filterCategoryLabel}>Kiosk Name</MuiTypography>
                    <Select
                      className={classes.filterInputField}
                      multiple
                      displayEmpty
                      value={kioskIdsToFilter}
                      onChange={(e) => handleSelectionChange('kiosk', e)}
                      input={<OutlinedInput />}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      renderValue={(selected: any) => {
                        if (selected.length === 0) {
                          return <Typography>Select Kiosk(s)</Typography>;
                        }
                        return selected
                          .map((selectedId: string) => {
                            const selectedKiosk = kioskList.find((k: Kiosk) => k.id === selectedId);
                            return selectedKiosk ? selectedKiosk.name : '';
                          })
                          .join(', ');
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 224,
                            width: 250
                          }
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left'
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left'
                        }
                      }}
                      inputProps={{ 'aria-label': 'Without label' }}
                    >
                      <MenuItem disabled value="">
                        <Typography>Select Kiosk(s)</Typography>
                      </MenuItem>
                      {kioskList.map((kiosk: Kiosk) => (
                        <MenuItem key={kiosk.id} value={kiosk.id} style={getStyles(kiosk.id, kioskIdsToFilter)}>
                          {kiosk.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </MuiBox>
                </Grid>
                {/* User filter */}
                <Grid item>
                  <MuiBox className={classes.filterCategoryBox}>
                    <MuiTypography className={classes.filterCategoryLabel}>User</MuiTypography>
                    <Select
                      className={classes.filterInputField}
                      multiple
                      displayEmpty
                      value={usersToFilter}
                      onChange={(e) => handleSelectionChange('user', e)}
                      input={<OutlinedInput />}
                      // eslint-disable-next-line @typescript-eslint/no-explicit-any
                      renderValue={(selected: any) => {
                        if (selected.length === 0) {
                          return <Typography>Select User(s)</Typography>;
                        }

                        return selected.join(', ');
                      }}
                      MenuProps={{
                        PaperProps: {
                          style: {
                            maxHeight: 224,
                            width: 250
                          }
                        },
                        anchorOrigin: {
                          vertical: 'bottom',
                          horizontal: 'left'
                        },
                        transformOrigin: {
                          vertical: 'top',
                          horizontal: 'left'
                        }
                      }}
                      inputProps={{ 'aria-label': 'Without label' }}
                    >
                      <MenuItem disabled value="">
                        <Typography>Select User(s)</Typography>
                      </MenuItem>
                      {admins.map((admin: AdminWithActivity) => (
                        <MenuItem key={admin.id} value={admin.name} style={getStyles(admin.name, usersToFilter)}>
                          {admin.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </MuiBox>
                </Grid>
              </Grid>
              {/* Reset button */}
              <Grid className={classes.resetFiltersButton}>
                <Flex direction="column">
                  <Button
                    size="small"
                    isDisabled={disableButton}
                    onClick={resetFilters}
                    data-testid="reset-filters-button"
                  >
                    Reset
                  </Button>
                </Flex>
              </Grid>
            </Grid>
          </>
        </Grid>
      </Grid>
      {/* Apply button */}
      <Grid className={classes.applyFiltersButton}>
        <Flex direction="column">
          <Button size="small" data-testid="apply-filters-button" onClick={() => onApply()}>
            Apply
          </Button>
        </Flex>
      </Grid>
    </Drawer>
  );
};
